import { Check } from "@similarweb/icons";
import type { ColumnsType } from "@similarweb/pro-logic-components";
import type { StrictColor } from "@similarweb/styles";
import { colorsPalettes, rgba } from "@similarweb/styles";
import { ChangeValue } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { chosenSites } from "app/common/services/chosenSites";
import { translate } from "app/filters/i18nFilter";
import { minimumValueChangeFilter, percentageSignFilter } from "app/filters/ngFilters";
import { getFaviconUrl } from "app/services/sitesService/sitesUtils";

import { ColorCircle } from "../../../common/StyledComponents";

import { QueryBarItemImageStyled } from "./styled";

interface IChartDataItem {
    domain: string;
    color: StrictColor;
    y: number;
    share: number;
    change: number;
    selected?: boolean;
}

export type TableDataType = {
    label: string;
    value: string[];
    [key: string]: unknown;
};
export type TableColumns = ColumnsType<TableDataType>;

export const generateSpendImpressionsColumnConfig = (domains: string[]): TableColumns => {
    const domainColumns: TableColumns = domains.map((domain) => {
        return {
            dataIndex: domain,
            resize: false,
            ellipsis: false,
            sort: false,
            tooltipTitle: domain,
            align: "center",
            title: () => (
                <QueryBarItemImageStyled
                    image={getFaviconUrl(domain)}
                    description={domain}
                    badgeColor={chosenSites.getSiteColor(domain)}
                    borderColor={colorsPalettes.carbon["50"]}
                />
            ),
            render: (_value, row) =>
                row.value.includes(domain) ? (
                    <Check
                        size="sm"
                        color={colorsPalettes.blue[400]}
                    />
                ) : null,
        };
    });

    return [
        {
            dataIndex: "label",
            title: "Spend",
            width: 120,
            render: (_v, row) => row.label,
        },
        ...domainColumns,
    ];
};

export const generateDonutTableColumnConfig = (): ColumnsType<IChartDataItem> => [
    {
        dataIndex: "domain",
        title: () => translate("ad.intelligence.advertiser.overview.breakdown.widget.domain.title"),
        resize: false,
        render: (_value, row) => (
            <Flex
                alignItems="center"
                gap="12px"
            >
                <ColorCircle color={row.color} />
                <Text
                    fontVariant="body2"
                    color={rgba(colorsPalettes.carbon[500], 0.8) as StrictColor}
                >
                    {row.domain}
                </Text>
            </Flex>
        ),
    },
    {
        dataIndex: "share",
        title: "Share",
        width: 64,
        align: "right",
        resize: false,
        render: (value: number) => percentageSignFilter()(value, 0) as string,
    },
    {
        dataIndex: "change",
        title: "PoP",
        width: 96,
        align: "right",
        resize: false,
        render: (value: number) => (
            <ChangeValue
                value={minimumValueChangeFilter()(value, 0)}
                isDecrease={value < 0}
            />
        ),
    },
];
